<template>
    <LoadingComponent :props="loading" />
    <div class="max-container px-2 sm:pt-10 sm:pb-0 sm:px-0" @click="handleUserActivity" @mousemove="handleUserActivity">
        <!--========DEMO MODE ALERT START=============-->
        <div v-if="showDemoAlert && isDemo"
            class="mt-4 mb-4 bg-amber-100 border border-amber-500 text-amber-600 p-4 rounded-lg flex items-center justify-between shadow-sm transition-all">
            <div>
                <h2 class="m-0 font-title text-amber-600 font-semibold">{{ $t('label.reminder') }}</h2>
                <p class="text-amber-600 font-sans text-sm mt-0.5 leading-5">{{ $t('label.data_reset') }}</p>
            </div>
            <button type="button" @click="showDemoAlert = false" class="text-amber-500 hover:text-amber-600 p-1.5 transition-colors" title="Cerrar aviso">
                <i class="fa-solid fa-xmark text-md"></i>
            </button>
        </div>
        <!--========DEMO MODE ALERT END=============-->

        <!--========WELCOME & WARNING HEADER START=============-->
        <div v-show="visibilitySettings.welcome_block || (visibilitySettings.order_alert_block && showWarningAlert)"
            class="w-full mt-3 mb-6 font-client border-b border-gray-200 pb-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div v-show="visibilitySettings.welcome_block">
                <h3 class="font-normal text-sm mt-2">{{ visitorMessage() }} {{ $t('label.welcome') }}</h3>
                <h4 class="font-medium text-xl text-primary">{{ authInfo.name }}</h4>
            </div>

            <!-- Warning Alert Notification -->
            <div v-if="showWarningAlert && visibilitySettings.order_alert_block"
                class="bg-primary/10 text-primary px-4 py-3 sm:py-2 rounded-lg flex items-center justify-between gap-4 text-sm transition-all">
                <div class="flex items-center gap-2.5">
                    <i class="lab-fill-document text-primary text-xl flex-shrink-0"></i>
                    <span class="font-medium">
                        {{ $t('message.pending_and_new_orders_alert', { pending: pendingOrdersCount, new_orders: todayOrdersCount }) }}
                    </span>
                </div>
                <button type="button" @click="showWarningAlert = false" class="text-primary-light hover:text-primary p-1 transition-colors flex-shrink-0" title="Cerrar notificación">
                    <i class="fa-solid fa-xmark text-md"></i>
                </button>
            </div>
        </div>
        <!--========WELCOME & WARNING HEADER END=============-->

        <div v-if="isReorderModeActive" class="w-full flex items-center justify-between bg-primary/10 border border-primary/50 px-3 py-2 rounded-md mb-3 cursor-move text-xs font-semibold text-primary">
            <span class="flex items-center gap-2">
                <i class="lab-line-menu text-md pt-0.5"></i> {{ $t('label.drag_to_reorder_vertical') }}
            </span>
        </div>

        <!--========OVERVIEW CARDS START=============-->
        <OverviewComponent v-show="visibilitySettings.overview_cards_block" :isReorderModeActive="isReorderModeActive" @activity="resetReorderTimer" />
        <!--========OVERVIEW CARDS END=============-->

        <!--========SUMMARY CHARTS START (SALES & LIVE ACTIVITY FEED)=============-->
        <div v-show="visibilitySettings.sales_summary_chart || visibilitySettings.live_activity_block">
            <draggable v-model="summaryChartsOrder" item-key="key" class="row mb-3" :disabled="!isReorderModeActive" @change="saveLayoutOrders">
                <template #item="{ element }">
                    <div v-show="(element.key === 'sales' && visibilitySettings.sales_summary_chart) || (element.key === 'live_activity' && visibilitySettings.live_activity_block)"
                        :class="summaryChartColClass" class="relative">
                        <div v-if="isReorderModeActive" class="absolute w-full -top-1 left-0 z-20 flex justify-center">
                            <div v-if="isReorderModeActive" class="cursor-move bg-white hover:bg-gray-50 text-xs p-2 rounded-md shadow flex items-center gap-1">
                                <i class="lab-line-menu text-md pt-0.5"></i>
                            </div>
                        </div>
                        <SalesSummaryComponent v-if="element.key === 'sales'" />
                        <LiveActivityComponent v-if="element.key === 'live_activity'" />
                    </div>
                </template>
            </draggable>
        </div>
        <!--========SUMMARY CHARTS END=============-->

        <!--========COMPLEMENTARY STATS TOGGLE & DASHBOARD CONTROL HEADER START=============-->
        <div v-show="visibilitySettings.extra_stats_block"
            class="w-full border-t border-b border-gray-200 py-4 px-0 mt-3 mb-6 bg-transparent rounded-lg flex flex-wrap sm:items-center justify-between gap-3">
            <div class="flex items-center gap-2">
                <button type="button" @click="showExtraStats = !showExtraStats"
                        :class="showExtraStats ? 'bg-gray-100' : 'bg-white'"
                    class="inline-flex items-center gap-2 px-4 py-2 text-xs font-medium border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 transition-all">
                    <span>{{ showExtraStats ? $t('label.hide_complementary_stats') : $t('label.show_complementary_stats') }}</span>
                    <i :class="showExtraStats ? 'lab-line-chevron-up' : 'lab-line-chevron-down'" class="text-xs"></i>
                </button>
            </div>

            <!-- Action buttons: Customize Dashboard & Reorder Mode Switch -->
            <div class="flex items-center gap-3">
                <button type="button" @click="showCustomizeModal = true"
                        :class="showCustomizeModal ? 'bg-gray-100' : 'bg-white'"
                    class="inline-flex items-center gap-2 px-3.5 py-2 text-xs font-medium border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 transition-all">
                    <i class="lab-line-edit text-sm"></i>
                    <span class="hidden sm:inline-block">{{ $t('label.customize_dashboard') }}</span>
                </button>

                <div class="flex items-center gap-2 relative">
                    <button type="button" @click="toggleReorderMode"
                        :class="isReorderModeActive ? 'bg-gray-100' : 'bg-white'"
                        class="inline-flex items-center gap-2 px-3 py-2 text-xs font-medium rounded-lg shadow-sm text-heading border border-gray-300 hover:bg-gray-50 transition-all"
                        :title="$t('label.reorder_mode_auto_off_notice')">
                        <i class="lab-line-settings text-sm" :class="{ 'animate-spin': isReorderModeActive }"></i>
                        <span v-if="isReorderModeActive" class="bg-white/20 px-1.5 py-0.5 rounded text-[10px] hidden">{{ reorderCountdown }}s</span>
                    </button>
                </div>
            </div>
        </div>
        <!--========COMPLEMENTARY STATS TOGGLE & DASHBOARD CONTROL HEADER END=============-->

        <!--========COLLAPSIBLE STATS CONTENT START (SLIDE-FADE & DRAGGABLE ROWS)=============-->
        <Transition name="slide-fade">
            <div v-show="visibilitySettings.extra_stats_block && showExtraStats">

                <div v-if="isReorderModeActive" class="w-full flex items-center justify-between bg-primary/10 border border-primary/50 px-3 py-2 rounded-md mb-3 cursor-move text-xs font-semibold text-primary">
                    <span class="flex items-center gap-2">
                        <i class="lab-line-menu text-md pt-0.5"></i> {{ $t('label.drag_to_reorder_vertical') }}
                    </span>
                </div>

                <draggable v-model="lowerRowsOrder" item-key="key" :disabled="!isReorderModeActive" @change="saveLayoutOrders">
                    <template #item="{ element: rowItem }">
                        <div class="relative mb-3">

                            <!-- Row 1: Order statistics cards -->
                            <div v-if="rowItem.key === 'order_stats' && visibilitySettings.order_stats_cards_subblock">
                                <OrderStatisticsComponent :isReorderModeActive="isReorderModeActive" @activity="resetReorderTimer" />
                            </div>

                            <!-- Row 2: Customer stats chart & Order summary chart side by side -->
                            <div v-if="rowItem.key === 'customer_row' && (visibilitySettings.customer_stats_subblock || visibilitySettings.order_summary_chart)">
                                <draggable v-model="customerRowOrder" item-key="key" class="row" :disabled="!isReorderModeActive" @change="saveLayoutOrders">
                                    <template #item="{ element: custItem }">
                                        <div v-show="(custItem.key === 'customer_stats' && visibilitySettings.customer_stats_subblock) || (custItem.key === 'orders' && visibilitySettings.order_summary_chart)"
                                            :class="customerSubblockColClass" class="relative">
                                            <CustomerStatsComponent v-if="custItem.key === 'customer_stats'" />
                                            <OrderSummaryComponent v-if="custItem.key === 'orders'" />
                                        </div>
                                    </template>
                                </draggable>
                            </div>

                            <!-- Row 3: Top customers table at full width (100%) -->
                            <div v-if="rowItem.key === 'top_customers' && visibilitySettings.top_customers_subblock" class="row">
                                <div class="col-12">
                                    <TopCustomersComponent />
                                </div>
                            </div>

                            <!-- Row 4: Most popular products carousel -->
                            <div v-if="rowItem.key === 'top_products' && visibilitySettings.top_products_subblock" class="row">
                                <TopProductsComponent />
                            </div>
                        </div>
                    </template>
                </draggable>
            </div>
        </Transition>
        <!--========COLLAPSIBLE STATS CONTENT END=============-->

        <!--========CUSTOMIZE DASHBOARD MODAL START=============-->
        <div v-if="showCustomizeModal" class="fixed inset-0 z-50 flex items-center justify-center bg-light/50 backdrop-blur margin-sidebar p-4 animate-fade-in animate-fade-out">
            <div class="bg-white rounded-xl shadow-lg w-full max-w-xl max-h-[480px] flex flex-col overflow-hidden  animate-scale-up">
                <!-- Modal Header (fijo arriba) -->
                <div class="p-6 pb-5 border-b flex items-center justify-between bg-gray-50 shrink-0">
                    <div class="flex items-center gap-2">
                        <i class="lab-line-edit text-primary text-xl"></i>
                        <h3 class="font-medium text-lg text-heading m-0">{{ $t('label.customize_dashboard_title') }}</h3>
                    </div>
                    <button type="button" @click="showCustomizeModal = false" class="text-gray-400 hover:text-heading transition-colors">
                        <i class="fa-solid fa-xmark text-lg"></i>
                    </button>
                </div>

                <!-- Modal Body (Toggle list with custom switches) -->
                <div class="p-6 overflow-y-auto flex-1 thin-scrolling flex flex-col gap-4">
                    <!-- Vista General -->
                    <div class="border border-gray-200 rounded-lg p-4 bg-white">
                        <h4 class="font-medium text-xs uppercase tracking-wider mb-3">{{ $t('label.general_view') }}</h4>
                        <div class="flex flex-col gap-2">
                            <div class="flex items-center justify-between">
                                <span class="text-xs font-sans font-medium">{{ $t('label.welcome_block') }}</span>
                                <button type="button" role="switch" :aria-checked="visibilitySettings.welcome_block"
                                    @click="visibilitySettings.welcome_block = !visibilitySettings.welcome_block; saveVisibilitySettings()"
                                    :class="visibilitySettings.welcome_block ? 'bg-primary border-primary/50' : 'bg-gray-100 border-gray-200'"
                                    class="relative inline-flex flex-shrink-0 h-5 w-9 border-2 rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none">
                                    <span :style="{ transform: visibilitySettings.welcome_block ? 'translateX(1rem)' : 'translateX(0px)' }"
                                        :class="visibilitySettings.welcome_block ? 'translate-x-4' : 'translate-x-0'"
                                        class="pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200"></span>
                                </button>
                            </div>

                            <div class="flex items-center justify-between border-t border-gray-200 pt-2">
                                <span class="text-xs font-sans font-medium">{{ $t('label.order_alert_block') }}</span>
                                <button type="button" role="switch" :aria-checked="visibilitySettings.order_alert_block"
                                    @click="visibilitySettings.order_alert_block = !visibilitySettings.order_alert_block; saveVisibilitySettings()"
                                    :class="visibilitySettings.order_alert_block ? 'bg-primary border-primary/50' : 'bg-gray-100 border-gray-200'"
                                    class="relative inline-flex flex-shrink-0 h-5 w-9 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none">
                                    <span :style="{ transform: visibilitySettings.order_alert_block ? 'translateX(1rem)' : 'translateX(0px)' }"
                                        :class="visibilitySettings.order_alert_block ? 'translate-x-4' : 'translate-x-0'"
                                        class="pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200"></span>
                                </button>
                            </div>

                            <div class="flex items-center justify-between border-t border-gray-200 pt-2">
                                <span class="text-xs font-sans font-medium">{{ $t('label.overview_cards_block') }}</span>
                                <button type="button" role="switch" :aria-checked="visibilitySettings.overview_cards_block"
                                    @click="visibilitySettings.overview_cards_block = !visibilitySettings.overview_cards_block; saveVisibilitySettings()"
                                    :class="visibilitySettings.overview_cards_block ? 'bg-primary border-primary/50' : 'bg-gray-100 border-gray-200'"
                                    class="relative inline-flex flex-shrink-0 h-5 w-9 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none">
                                    <span :style="{ transform: visibilitySettings.overview_cards_block ? 'translateX(1rem)' : 'translateX(0px)' }"
                                        :class="visibilitySettings.overview_cards_block ? 'translate-x-4' : 'translate-x-0'"
                                        class="pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200"></span>
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Gráficas de Resumen y Actividad -->
                    <div class="border border-gray-200 rounded-lg p-4 bg-white">
                        <h4 class="font-medium text-xs uppercase tracking-wider mb-3">{{ $t('label.summary_charts_block') }}</h4>
                        <div class="flex flex-col gap-2">
                            <div class="flex items-center justify-between">
                                <span class="text-xs font-sans font-medium">{{ $t('label.sales_summary_chart') }}</span>
                                <button type="button" role="switch" :aria-checked="visibilitySettings.sales_summary_chart"
                                    @click="visibilitySettings.sales_summary_chart = !visibilitySettings.sales_summary_chart; saveVisibilitySettings()"
                                    :class="visibilitySettings.sales_summary_chart ? 'bg-primary border-primary/50' : 'bg-gray-100 border-gray-200'"
                                    class="relative inline-flex flex-shrink-0 h-5 w-9 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none">
                                    <span :style="{ transform: visibilitySettings.sales_summary_chart ? 'translateX(1rem)' : 'translateX(0px)' }"
                                        :class="visibilitySettings.sales_summary_chart ? 'translate-x-4' : 'translate-x-0'"
                                        class="pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200"></span>
                                </button>
                            </div>

                            <div class="flex items-center justify-between border-t border-gray-200 pt-2">
                                <span class="text-xs font-sans font-medium">{{ $t('label.live_activity_block') }}</span>
                                <button type="button" role="switch" :aria-checked="visibilitySettings.live_activity_block"
                                    @click="visibilitySettings.live_activity_block = !visibilitySettings.live_activity_block; saveVisibilitySettings()"
                                    :class="visibilitySettings.live_activity_block ? 'bg-primary border-primary/50' : 'bg-gray-100 border-gray-200'"
                                    class="relative inline-flex flex-shrink-0 h-5 w-9 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none">
                                    <span :style="{ transform: visibilitySettings.live_activity_block ? 'translateX(1rem)' : 'translateX(0px)' }"
                                        :class="visibilitySettings.live_activity_block ? 'translate-x-4' : 'translate-x-0'"
                                        class="pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200"></span>
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Bloque Estadísticas Complementarias -->
                    <div class="border border-gray-200 rounded-lg p-4 bg-white">
                        <div class="flex items-center justify-between mb-3 pb-2 border-b border-gray-200">
                            <h4 class="font-medium text-xs uppercase tracking-wider m-0">{{ $t('label.extra_stats_block') }}</h4>
                            <button type="button" role="switch" :aria-checked="visibilitySettings.extra_stats_block"
                                @click="visibilitySettings.extra_stats_block = !visibilitySettings.extra_stats_block; saveVisibilitySettings()"
                                :class="visibilitySettings.extra_stats_block ? 'bg-primary border-primary/50' : 'bg-gray-100 border-gray-200'"
                                class="relative inline-flex flex-shrink-0 h-5 w-9 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none">
                                <span :style="{ transform: visibilitySettings.extra_stats_block ? 'translateX(1rem)' : 'translateX(0px)' }"
                                    :class="visibilitySettings.extra_stats_block ? 'translate-x-4' : 'translate-x-0'"
                                    class="pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200"></span>
                            </button>
                        </div>

                        <div v-show="visibilitySettings.extra_stats_block" class="flex flex-col gap-2">
                            <div class="flex items-center justify-between">
                                <span class="text-xs font-sans font-medium">{{ $t('label.order_stats_cards_subblock') }}</span>
                                <button type="button" role="switch" :aria-checked="visibilitySettings.order_stats_cards_subblock"
                                    @click="visibilitySettings.order_stats_cards_subblock = !visibilitySettings.order_stats_cards_subblock; saveVisibilitySettings()"
                                    :class="visibilitySettings.order_stats_cards_subblock ? 'bg-primary border-primary/50' : 'bg-gray-100 border-gray-200'"
                                    class="relative inline-flex flex-shrink-0 h-5 w-9 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none">
                                    <span :style="{ transform: visibilitySettings.order_stats_cards_subblock ? 'translateX(1rem)' : 'translateX(0px)' }"
                                        :class="visibilitySettings.order_stats_cards_subblock ? 'translate-x-4' : 'translate-x-0'"
                                        class="pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200"></span>
                                </button>
                            </div>

                            <div class="flex items-center justify-between border-t border-gray-200 pt-2">
                                <span class="text-xs font-sans font-medium">{{ $t('label.order_summary_chart') }}</span>
                                <button type="button" role="switch" :aria-checked="visibilitySettings.order_summary_chart"
                                    @click="visibilitySettings.order_summary_chart = !visibilitySettings.order_summary_chart; saveVisibilitySettings()"
                                    :class="visibilitySettings.order_summary_chart ? 'bg-primary border-primary/50' : 'bg-gray-100 border-gray-200'"
                                    class="relative inline-flex flex-shrink-0 h-5 w-9 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none">
                                    <span :style="{ transform: visibilitySettings.order_summary_chart ? 'translateX(1rem)' : 'translateX(0px)' }"
                                        :class="visibilitySettings.order_summary_chart ? 'translate-x-4' : 'translate-x-0'"
                                        class="pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200"></span>
                                </button>
                            </div>

                            <div class="flex items-center justify-between border-t border-gray-200 pt-2">
                                <span class="text-xs font-sans font-medium">{{ $t('label.customer_stats_subblock') }}</span>
                                <button type="button" role="switch" :aria-checked="visibilitySettings.customer_stats_subblock"
                                    @click="visibilitySettings.customer_stats_subblock = !visibilitySettings.customer_stats_subblock; saveVisibilitySettings()"
                                    :class="visibilitySettings.customer_stats_subblock ? 'bg-primary border-primary/50' : 'bg-gray-100 border-gray-200'"
                                    class="relative inline-flex flex-shrink-0 h-5 w-9 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none">
                                    <span :style="{ transform: visibilitySettings.customer_stats_subblock ? 'translateX(1rem)' : 'translateX(0px)' }"
                                        :class="visibilitySettings.customer_stats_subblock ? 'translate-x-4' : 'translate-x-0'"
                                        class="pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200"></span>
                                </button>
                            </div>

                            <div class="flex items-center justify-between border-t border-gray-200 pt-2">
                                <span class="text-xs font-sans font-medium">{{ $t('label.top_customers_subblock') }}</span>
                                <button type="button" role="switch" :aria-checked="visibilitySettings.top_customers_subblock"
                                    @click="visibilitySettings.top_customers_subblock = !visibilitySettings.top_customers_subblock; saveVisibilitySettings()"
                                    :class="visibilitySettings.top_customers_subblock ? 'bg-primary border-primary/50' : 'bg-gray-100 border-gray-200'"
                                    class="relative inline-flex flex-shrink-0 h-5 w-9 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none">
                                    <span :style="{ transform: visibilitySettings.top_customers_subblock ? 'translateX(1rem)' : 'translateX(0px)' }"
                                        :class="visibilitySettings.top_customers_subblock ? 'translate-x-4' : 'translate-x-0'"
                                        class="pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200"></span>
                                </button>
                            </div>

                            <div class="flex items-center justify-between border-t border-gray-200 pt-2">
                                <span class="text-xs font-sans font-medium">{{ $t('label.top_products_subblock') }}</span>
                                <button type="button" role="switch" :aria-checked="visibilitySettings.top_products_subblock"
                                    @click="visibilitySettings.top_products_subblock = !visibilitySettings.top_products_subblock; saveVisibilitySettings()"
                                    :class="visibilitySettings.top_products_subblock ?  'bg-primary border-primary/50' : 'bg-gray-100 border-gray-200'"
                                    class="relative inline-flex flex-shrink-0 h-5 w-9 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none">
                                    <span :style="{ transform: visibilitySettings.top_products_subblock ? 'translateX(1rem)' : 'translateX(0px)' }"
                                        :class="visibilitySettings.top_products_subblock ? 'translate-x-4' : 'translate-x-0'"
                                        class="pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200"></span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Modal Footer (fijo abajo) -->
                <div class="px-6 py-4 border-t bg-gray-50 flex items-center justify-between shrink-0">
                    <button type="button" @click="resetVisibilityDefaults" class="text-xs font-semibold text-gray-600 hover:text-primary transition-colors">
                        {{ $t('label.reset_default') }}
                    </button>
                    <button type="button" @click="showCustomizeModal = false" class="px-5 py-2 text-xs font-semibold text-white bg-primary rounded-lg shadow-sm hover:opacity-90 transition-all">
                        {{ $t('button.save') }}
                    </button>
                </div>
            </div>
        </div>
        <!--========CUSTOMIZE DASHBOARD MODAL END=============-->
    </div>
</template>

<script>
import LoadingComponent from "../components/LoadingComponent";
import OverviewComponent from "./OverviewComponent";
import OrderStatisticsComponent from "./OrderStatisticsComponent";
import SalesSummaryComponent from "./SalesSummaryComponent";
import OrderSummaryComponent from "./OrderSummaryComponent";
import CustomerStatsComponent from "./CustomerStatsComponent";
import TopCustomersComponent from "./TopCustomersComponent";
import TopProductsComponent from "./TopProductsComponent";
import LiveActivityComponent from "./LiveActivityComponent";
import draggable from "vuedraggable";
import ENV from "../../../config/env";

export default {
    name: "DashboardComponent",
    components: {
        LoadingComponent,
        OverviewComponent,
        OrderStatisticsComponent,
        SalesSummaryComponent,
        OrderSummaryComponent,
        CustomerStatsComponent,
        TopCustomersComponent,
        TopProductsComponent,
        LiveActivityComponent,
        draggable,
    },
    data() {
        return {
            loading: {
                isActive: false,
            },
            demo: ENV.DEMO,
            showDemoAlert: true,
            showWarningAlert: true,
            showExtraStats: false,
            showCustomizeModal: false,
            isReorderModeActive: false,
            reorderCountdown: 60,
            reorderTimer: null,
            reorderInterval: null,
            visibilitySettings: {
                welcome_block: true,
                order_alert_block: true,
                overview_cards_block: true,
                sales_summary_chart: true,
                live_activity_block: true,
                order_summary_chart: true,
                extra_stats_block: true,
                order_stats_cards_subblock: true,
                customer_stats_subblock: true,
                top_customers_subblock: true,
                top_products_subblock: true,
            },
            summaryChartsOrder: [
                { key: 'live_activity' },
                { key: 'sales' }
            ],
            customerRowOrder: [
                { key: 'customer_stats' },
                { key: 'orders' }
            ],
            lowerRowsOrder: [
                { key: 'order_stats' },
                { key: 'customer_row' },
                { key: 'top_customers' },
                { key: 'top_products' }
            ]
        };
    },
    computed: {
        authInfo: function () {
            return this.$store.getters.authInfo;
        },
        kpis: function () {
            return this.$store.getters["dashboard/commerceKpis"] || {};
        },
        pendingOrdersCount: function () {
            return this.kpis.pending_orders ?? 0;
        },
        todayOrdersCount: function () {
            return this.kpis.orders_today ?? 0;
        },
        isDemo: function () {
            return this.demo === 'true' || this.demo === 'TRUE' || this.demo === 'True' || this.demo === true || this.demo === '1' || this.demo === 1;
        },
        summaryChartColClass: function () {
            if (this.visibilitySettings.sales_summary_chart && this.visibilitySettings.live_activity_block) {
                return 'col-12 xl:col-6';
            }
            return 'col-12';
        },
        customerSubblockColClass: function () {
            if (this.visibilitySettings.customer_stats_subblock && this.visibilitySettings.order_summary_chart) {
                return 'col-12 xl:col-6';
            }
            return 'col-12';
        }
    },
    mounted() {
        this.fetchKpis();
        this.initDemoTimer();
        this.loadSavedVisibilitySettings();
        this.loadSavedLayoutOrders();
    },
    beforeUnmount() {
        this.clearReorderTimers();
    },
    methods: {
        fetchKpis: function () {
            this.$store.dispatch("dashboard/commerceKpis");
        },
        initDemoTimer: function () {
            if (this.isDemo) {
                setTimeout(() => {
                    this.showDemoAlert = false;
                }, 4500);
            }
        },
        toggleReorderMode: function () {
            this.isReorderModeActive = !this.isReorderModeActive;
            if (this.isReorderModeActive) {
                this.resetReorderTimer();
            } else {
                this.clearReorderTimers();
            }
        },
        handleUserActivity: function () {
            if (this.isReorderModeActive) {
                this.resetReorderTimer();
            }
        },
        resetReorderTimer: function () {
            this.clearReorderTimers();
            this.reorderCountdown = 30;

            this.reorderInterval = setInterval(() => {
                if (this.reorderCountdown > 1) {
                    this.reorderCountdown--;
                }
            }, 1000);

            this.reorderTimer = setTimeout(() => {
                this.isReorderModeActive = false;
                this.clearReorderTimers();
            }, 30000);
        },
        clearReorderTimers: function () {
            if (this.reorderTimer) clearTimeout(this.reorderTimer);
            if (this.reorderInterval) clearInterval(this.reorderInterval);
            this.reorderTimer = null;
            this.reorderInterval = null;
        },
        loadSavedVisibilitySettings: function () {
            try {
                const saved = localStorage.getItem('admin_dashboard_visibility_settings');
                if (saved) {
                    this.visibilitySettings = { ...this.visibilitySettings, ...JSON.parse(saved) };
                }
            } catch (e) {
                console.error("Error loading visibility settings", e);
            }
        },
        saveVisibilitySettings: function () {
            try {
                localStorage.setItem('admin_dashboard_visibility_settings', JSON.stringify(this.visibilitySettings));
            } catch (e) {
                console.error("Error saving visibility settings", e);
            }
        },
        resetVisibilityDefaults: function () {
            Object.keys(this.visibilitySettings).forEach(k => {
                this.visibilitySettings[k] = true;
            });
            this.saveVisibilitySettings();
        },
        loadSavedLayoutOrders: function () {
            try {
                const charts = localStorage.getItem('admin_dashboard_charts_order');
                if (charts) {
                    const parsed = JSON.parse(charts);
                    // Solo restaurar si contiene la clave y el usuario ya lo reordenó explícitamente
                    if (parsed.some(item => item.key === 'live_activity') && parsed.some(item => item.key === 'sales')) {
                        this.summaryChartsOrder = parsed;
                    }
                }

                const custRow = localStorage.getItem('admin_dashboard_cust_row_order');
                if (custRow) {
                    const parsed = JSON.parse(custRow);
                    if (parsed.some(item => item.key === 'orders')) {
                        this.customerRowOrder = parsed;
                    }
                }

                const lowerRows = localStorage.getItem('admin_dashboard_lower_rows_order');
                if (lowerRows) {
                    const parsed = JSON.parse(lowerRows);
                    if (parsed.some(item => item.key === 'top_customers')) {
                        this.lowerRowsOrder = parsed;
                    }
                }
            } catch (e) {
                console.error("Error loading layout orders", e);
            }
        },
        saveLayoutOrders: function () {
            try {
                localStorage.setItem('admin_dashboard_charts_order', JSON.stringify(this.summaryChartsOrder));
                localStorage.setItem('admin_dashboard_cust_row_order', JSON.stringify(this.customerRowOrder));
                localStorage.setItem('admin_dashboard_lower_rows_order', JSON.stringify(this.lowerRowsOrder));
                this.resetReorderTimer();
            } catch (e) {
                console.error("Error saving layout orders", e);
            }
        },
        getRowLabel: function (key) {
            if (key === 'order_stats') return this.$t('label.order_stats_cards_subblock');
            if (key === 'customer_row') return `${this.$t('label.customer_stats_subblock')} / ${this.$t('label.order_summary_chart')}`;
            if (key === 'top_customers') return this.$t('label.top_customers_subblock');
            if (key === 'top_products') return this.$t('label.top_products_subblock');
            return key;
        },
        visitorMessage: function () {
            let greet;
            let myDate = new Date();
            let hrs = myDate.getHours();
            if (hrs < 12) {
                greet = this.$t('message.good_morning');
            } else if (hrs >= 12 && hrs <= 17) {
                greet = this.$t('message.good_afternoon');
            } else if (hrs >= 17 && hrs <= 24) {
                greet = this.$t('message.good_evening');
            }
            return greet;
        }
    }
}
</script>

<style scoped>
.slide-fade-enter-active {
    transition: all 0.35s ease-out;
}
.slide-fade-leave-active {
    transition: all 0.3s cubic-bezier(1, 0.5, 0.8, 1);
}
.slide-fade-enter-from,
.slide-fade-leave-to {
    transform: translateY(-10px);
    opacity: 0;
}

.translate-x-4 {
    transform: translateX(1rem) !important;
}

.translate-x-0 {
    transform: translateX(0px) !important;
}
</style>
